import React, {useState, useEffect} from "react";
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import {useForm} from "react-hook-form";
import { EMAIL_PATTERN, JSON_HEADERS, COMPANY_NAME } from "../../constants/constants";
import Form from "react-bootstrap/Form";
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import UserCard from "../users/UserCard";
import ModalClose from "../modals/ModalClose";
import {createElementFromHTML, validateEmail} from "../../utils/utilsHelpers";
import SectionHeaderTag from "../misc/SectionHeaderTag";
import {ProgressBar} from "react-bootstrap";
import styled from "styled-components";
import { isDesktop } from "react-device-detect";
import {objectMap, articleFor, objectForEach} from "../../../javascripts/utils";
import Bro from '../../../images/bro.svg';
import chunk from 'lodash/chunk';
import {useGoogleReCaptcha} from "react-google-recaptcha-v3";
import he from 'he';

const CustomModalDialog = styled(Modal.Dialog)`
        max-width: 690px; 
    `;

export default function PeerTalkForm(props) {

    const [isEmailValid, setIsEmailValid] = useState(false);
    const [emailError, setEmailError] = useState(null);
    const [submitCta, setSubmitCta] = useState('Request an inquiry call');
    const [activeStep, setActiveStep] = useState(0);
    const [messageSent, setMessageSent] = useState(false);
    const { register, handleSubmit, clearErrors, watch, setError, setValue, formState: { errors, isValid, isSubmitting }} = useForm({ mode: 'onBlur'});
    const currentExpert = props.selectedExpert;
    const registeredQuestions = watch('questions');
    const { executeRecaptcha } = useGoogleReCaptcha();

    useEffect(() => {
        setActiveStep(0);
        setMessageSent(false);
    }, [currentExpert]);

    const thumbImages = [Bro];
    for (const image of thumbImages) {
        const imageElement = new Image();
        imageElement.src = image;
    }

    const onMessageSubmit = async(data) => {
        if(props.answeredQuestions) {
            data.smarter_question_responses = props.answeredQuestions;
            data.questions = {};
            objectForEach(props.answeredQuestions, (q, _) => {
                data.questions[q.field_symbol] = q.response
            });
        } else {
            data.smarter_question_responses = objectMap(data.questions, (answer, question) => {
                return { field_symbol: question, response: answer }
            });
        }
        const requestItems = { ...data };

        if(props.renderAsLeadGen) {
            if(props.currentUser) {
                data.user = props.currentUser;
                if(props.hiddenTags) {
                    objectMap(props.hiddenTags, (value, key) => {
                        data[key] = value;
                    });
                }

                let captchaToken = "";
                try {
                    captchaToken = await executeRecaptcha('send_report');
                } catch (error) {
                    console.error('Error during recaptcha verification:', error);
                }
                const payload = {...data, "g-recaptcha-response-data": { "send_report": captchaToken }};

                await fetch('/landing/create_lead', {
                    method: 'POST',
                    headers: JSON_HEADERS,
                    body: JSON.stringify(payload)
                }).then(function(response) {
                    return response.json();
                }).then(function(jsonResponse) {
                    if(jsonResponse.success) {
                        props.onLeadCreate({...jsonResponse, ...data });
                    }
                });
            } else {
                props.onNextModal({ additionalLeadData: requestItems });
            }
        } else {
            delete requestItems.questions;
            setSubmitCta('Sending...');
            await fetch(`/users/${currentExpert.id}/send_peer_talk_message`, {
                method: 'POST',
                headers: JSON_HEADERS,
                body: JSON.stringify({...requestItems, cancel: props.isCancelling})
            }).then(_ => {
                if(!props.answeredQuestions) props.onSmarterQuestionsAnswered(data.smarter_question_responses, props.productId);
                if(props.isCancelling) props.onMessageSent();
                else setMessageSent(true);
            });
        }

        dataLayer.push({'event': 'PeerTalk_form_success' });
    }

    const onCloseConfirmation = () => {
        setMessageSent(false);
        props.onMessageSent();
    }

    const renderMessageField = () => {
        return (
            <Form.Group className="mb-3">
                <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder={'Fill in your message'}
                    {...register('message', {
                        required: 'This field is required'
                    })}
                />
            </Form.Group>
        )
    }

    const renderEmailField = () => {
        return (
            <Form.Group className="mb-3 text-start">
                <Form.Control
                    type="email"
                    id="email"
                    placeholder="Company email"
                    defaultValue={props.currentUser?.email}
                    onKeyUp={(e) => validateEmail(e.target.value, 'from_email', isEmailValid, setIsEmailValid, setEmailError, setError, clearErrors)}
                    {...register("from_email", {
                        required: 'This field is required',
                        pattern: {
                            value: EMAIL_PATTERN,
                            message: "Please enter a valid email address"
                        },
                        validate: () => isEmailValid || emailError
                    })} />
                {errors.from_email && <Form.Text className="text-danger">{errors.from_email.message}</Form.Text>}
            </Form.Group>
        )
    }

    const renderHiddenFields = () => {
        let hiddenInputs = props.hiddenTags && objectMap(props.hiddenTags, (value, name) => {
            return <input type='hidden' id={name} key={name} value={value} {...register(name)} />
        });

        return (
            <>
                <input type='hidden' id='product_id' key='product_id' value={props.productId} {...register('product_id')} />
                <input type='hidden' id='category_id' key='category_id' value={props.categoryId} {...register('category_id')} />
                <input type='hidden' id='authenticity_token' key='authenticity_token' value={props.token} {...register('authenticity_token')} />
                { hiddenInputs?.length && hiddenInputs }
            </>
        )
    }

    const renderVerifiedUser = () => {
        return (
            <div className={'d-flex align-items-center pt-1 pb-3 mt-1 border-top'}>
                <i className={'far fa-badge-check text-success pe-1'}></i>
                <span className={'text-secondary'}>Verified user of {props.productName}</span>
            </div>
        )
    }

    const renderMessageBox = () => {
        return (
            <div className={'p-4 m-2'}>
                <UserCard
                    user={currentExpert}
                    showAvatarDot={true}
                    nameClasses={'text-medium font-14 fw-light'}
                    userDetailsClasses={'text-secondary fw-bolder font-15'}
                    forceHeight={false}
                    renderAsLink={false} />
                {renderVerifiedUser()}
                <Form
                    noValidate={true}
                    acceptCharset='UTF-8'
                    onSubmit={handleSubmit(onMessageSubmit)}
                >
                    {props.showEmail && renderEmailField()}
                    {renderMessageField()}
                    {/*{renderGdpr()}*/}
                    {renderHiddenFields()}
                    <Button variant="primary" type="submit" className={'btn-block btn-hover-cursor'} disabled={isSubmitting}>
                        {submitCta}
                    </Button>
                </Form>
            </div>
        )
    }

    const renderConfirmCancel = () => {
        return (
            <div className={'p-4 m-2'}>
                <div className={'fs-5 text-secondary fw-bold'}>You are requesting to cancel your call request with:</div>
                <UserCard
                    user={currentExpert}
                    showAvatarDot={true}
                    nameClasses={'text-medium font-14 fw-light'}
                    userDetailsClasses={'text-secondary fw-bolder font-15'}
                    renderAsLink={false} />
                <Form
                    noValidate={true}
                    acceptCharset='UTF-8'
                    onSubmit={handleSubmit(onMessageSubmit)}
                >
                    <input type='hidden' id='product_id' key='product_id' value={props.productId} {...register('product_id')} />
                    <input type='hidden' id='category_id' key='category_id' value={props.categoryId} {...register('category_id')} />
                    <input type='hidden' id='authenticity_token' key='authenticity_token' value={props.token} {...register('authenticity_token')} />
                    <Button variant="primary" type="submit" className={'btn-block btn-hover-cursor mt-3'} disabled={isSubmitting}>
                        Confirm cancel request
                    </Button>
                </Form>
            </div>
        )
    }

    const renderQuestion = () => {
        let activeQuestion = props.questions[activeStep];
        let unanswered = registeredQuestions && !registeredQuestions[activeQuestion.field_symbol]
        if(activeQuestion?.response_options) {
            const chunkSize = activeQuestion.response_options.length > 5 ?
                Math.ceil(activeQuestion.response_options.length / 2) : activeQuestion.response_options.length;
            const colResponses = chunk(activeQuestion.response_options, chunkSize);

            return (
                <>
                    <Row className={'my-2 mx-0'}>
                        <Col sm={2} md={1} className={'avatar-xs rounded-circle bg-white text-center d-none d-sm-flex'} style={{ border: '1px solid #E5E6E1' }}>
                            <span className={'fs-6 avatar-title text-secondary content-styling'}>{activeStep + 1}</span>
                        </Col>
                        <Col sm={10} md={11}>
                            <div className={'text-secondary fs-6 fw-bolder mb-2 fs-6'}>{activeQuestion.question}</div>
                            <Row style={ isDesktop ? { height: '145px'} : {}}>
                                {colResponses.map((responseOptions, i) => {
                                    return (
                                        <Col sm={6} key={`question-${activeStep}-${i}`}>
                                            {responseOptions.map((response, index) => {
                                                const uniqueId = `response-input-${activeStep}-${i}-${index}`;
                                                return (
                                                    <Form.Check type={'radio'} className={'form-check-primary my-1'} key={uniqueId}>
                                                        <Form.Check.Input
                                                            type={'radio'}
                                                            id={uniqueId}
                                                            defaultValue={response}
                                                            defaultChecked={registeredQuestions?.[activeQuestion.field_symbol] === response}
                                                            {...register(`questions.${activeQuestion.field_symbol}`)}
                                                        />
                                                        <Form.Check.Label className={'content-styling text-secondary fs-6 lh-1'} htmlFor={uniqueId}>{response}</Form.Check.Label>
                                                    </Form.Check>
                                                )
                                            })}
                                        </Col>
                                    )
                                })}
                            </Row>
                        </Col>
                    </Row>
                    <Row className={'my-2 justify-content-end'}>
                        {activeStep ? <Col sm={6} lg={3}>
                            <Button variant={'link'} size={'sm'} className={'btn-block text-secondary'} onClick={() => setActiveStep(activeStep - 1)}>Back</Button>
                        </Col> : ''}
                        <Col sm={6} lg={3}>
                            <Button variant={'primary'}
                                    size={'sm'}
                                    className={'btn-block'}
                                    onClick={() => setActiveStep(activeStep + 1)}
                                    disabled={unanswered}>
                                Next
                            </Button>
                        </Col>
                    </Row>
                </>
            )
        }
    }

    const renderConfirmation = () => {
        return (
            <div className={'p-4 m-2'}>
                <SectionHeaderTag text={'Thank you,'}/>
                <div className={'subheader-sm my-3 text-secondary fs-5'}>Your request has been sent successfully.
                    We will contact the peer to coordinate the call and get back to you.</div>
                <div className={'d-flex justify-content-center'}>
                    <img src={Bro} />
                </div>
            </div>
        )
    }

    const renderPeerTalkWizard = () => {
        if(!props.answeredQuestions && activeStep < props.questions.length) {
            return (
                <div className={'p-4 m-2'}>
                    <SectionHeaderTag
                        text={`Request a call with ${articleFor(currentExpert.job_function)} ${he.decode(currentExpert.job_function)} ${currentExpert.industry ? 'working at' : ''} ${articleFor(currentExpert.industry)} ${currentExpert.industry.toLowerCase()}`}/>
                    <div className={'subheader-sm py-2'}>We would like to match you with the most
                        suitable {COMPANY_NAME} member for your needs.
                        To do so, please answer the following questions.
                    </div>
                    <ProgressBar now={((100 / props.questions.length) * activeStep)} style={{ height: '5px' }} className={'mb-2'} variant={'blue-link'}/>
                    {renderQuestion()}
                    <div className={'content-styling text-secondary fs-6 my-2'}>*This service is only available for qualified buyers.</div>
                </div>
            )
        } else if(!messageSent) {
            return (
                renderMessageBox()
            )
        }  else if(!props.renderAsLeadGen) {
            return (
                renderConfirmation()
            )
        }
    }

    if(props.renderAsLeadGen) {
        return (
            <>
                { props.isCancelling ? renderConfirmCancel() : renderPeerTalkWizard()}
            </>
        )
    } else {
        return (
            <Modal id={`reference-call`}
                   show={props.show}
                   onHide={() => props.setShow(false)}
                   centered={true}
                   size={'md'}
                   animation={true}
                   fullscreen={'md-down'}
                   dialogAs={CustomModalDialog}
                   style={ !isDesktop ? { zIndex: 99999, backgroundColor: 'white' } : {}}
                   contentClassName={`text-secondary`}>
                <ModalClose onClose={ messageSent ? onCloseConfirmation : props.setShow} />
                { props.isCancelling ? renderConfirmCancel() : renderPeerTalkWizard()}
            </Modal>
        )
    }
}